import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { getTenants, selectTenant } from '../modules/tenantdata';

const styles = {
    chip: {
        color: 'inherit',
        borderColor: 'rgba(255,255,255,0.5)',
        mr: 1,
        '& .MuiChip-deleteIcon': { color: 'inherit' },
    },
    flag: { mr: 0.5 },
    menuHeader: { px: 2, py: 1, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase' },
    itemInfo: { display: 'flex', flexDirection: 'column' },
    itemMeta: { fontSize: 12, color: 'text.secondary' },
    checkIcon: { ml: 2, color: 'secondary.main' },
};

// ISO3166-1 alpha-2 -> flag emoji, purely cosmetic. Falls back to a generic globe if unmapped.
const FLAG_BY_ALPHA2 = { FI: '🇫🇮', SE: '🇸🇪', EE: '🇪🇪' };
const flagFor = (alpha2) => FLAG_BY_ALPHA2[alpha2] || '🌐';

// Tenant/company switcher (see doc/sweden-expansion-currency-plan.md, Phase 1). Lists the
// TenantCompany rows the current user is assigned to (backend enforces this in
// TenantCompanyDataMapper.GetTenantsForUser) and lets them switch their active one.
function TenantSwitcher() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const tenants = useSelector((state) => state.tenantdata.tenants);
    const activeTenantId = useSelector((state) => state.tenantdata.activeTenantId);
    const countryCodes = useSelector((state) => state.codedata.countryCodes) || [];
    const activeTenant = tenants.find((tenant) => tenant.id === activeTenantId);

    // tenant.countryCode is the ISO3166-1 numeric code (see backendservices/domain/TenantCompany.cs);
    // resolve it to the alpha-2 code via the shared country-code list (loaded app-wide, see
    // dataloadermiddleware.js) for display/flag purposes.
    const alpha2For = (countryCode) => {
        const match = countryCodes.find((c) => c.countryId === countryCode);
        return match ? match.countryShortStr2 : null;
    };

    const open = Boolean(anchorEl);

    React.useEffect(() => {
        getTenants()(dispatch);
    }, [dispatch]);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (tenant) => {
        if (tenant.id !== activeTenantId) {
            selectTenant(tenant.id)(dispatch);
        }
        handleClose();
    };

    // Nothing to show until tenants have loaded, or if the user has only one tenant with no choice to make.
    if (!activeTenant || tenants.length < 2) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
                variant="outlined"
                sx={styles.chip}
                onClick={handleOpen}
                deleteIcon={<ArrowDropDownIcon />}
                onDelete={handleOpen}
                label={
                    <>
                        <span style={styles.flag}>{flagFor(alpha2For(activeTenant.countryCode))}</span>
                        {activeTenant.name} · {activeTenant.currency}
                    </>
                }
            />
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <Typography sx={styles.menuHeader}>{t('general.switchTenant')}</Typography>
                {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} onClick={() => handleSelect(tenant)}>
                        <span style={{ marginRight: 10, fontSize: 20 }}>{flagFor(alpha2For(tenant.countryCode))}</span>
                        <ListItemText
                            sx={styles.itemInfo}
                            primary={tenant.name}
                            secondary={
                                <span style={styles.itemMeta}>
                                    {alpha2For(tenant.countryCode) || tenant.countryCode} · {tenant.currency}
                                </span>
                            }
                        />
                        {tenant.id === activeTenantId && (
                            <CheckIcon sx={styles.checkIcon} fontSize="small" />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}

export default TenantSwitcher;
